import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { ProductDetail } from '../../models/product-detail';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Subscription } from 'rxjs';
import { FeatureToggleService } from '../../services/feature-toggle.service';
import { AppFacadeService } from '../../services/app-facade.service';
import { DocumentData } from '../../store/domains/document/document.reducers';
import { ModalService } from '../../services';
import { SendLinkRequest } from '../../models/send-link-request';
import { AppMediatorService } from '../../services/app-mediator.service';
import { ProductState } from '../../store/domains/product/product.reducers';
import { GetDocumentParam } from '../../models/contract-model';

@Component({
    standalone: false,
    selector: 'por-product-detail',
    templateUrl: './product-detail.component.html',
    styleUrls: ['./product-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductDetailComponent implements OnInit, OnDestroy {
    private readonly breakpointObserver = inject(BreakpointObserver);
    readonly featureToggleService = inject(FeatureToggleService);
    readonly appFacadeService = inject(AppFacadeService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly modal = inject(ModalService);

    @Input() productDetail?: ProductDetail;
    @Output() readonly closePanel = new EventEmitter<boolean>(false);
    @Input() uiUrl: string | undefined;
    /** When true, hides auth-only UI sections (payment actions, contract navigation). */
    @Input() isPublicView = false;
    isMobileLayout = false;
    subscriptions: Subscription[] = [];
    sendLinkRequestData: SendLinkRequest = {} as SendLinkRequest;
    customerId = '';
    contractId = '';
    documentParams!: GetDocumentParam;

    ngOnInit(): void {
        /**
         * Ref: https://wiki.pointofrental.com/e/en/Departments/Engineering/Consumer-Portal/Quick-Link
         */
        this.subscriptions.push(
            this.breakpointObserver.observe(['(max-width: 992px)']).subscribe((result: BreakpointState) => {
                this.isMobileLayout = result.matches;
            })
        );
        this.subscriptions.push(
            this.appFacadeService.getProductDetail().subscribe((productState: ProductState) => {
                this.productDetail = productState.productDetail?.[0];
                this.customerId = productState.customerId as string;
                this.contractId = productState.contractId as string;
                const documentValue = productState.documentValue as string;

                this.sendLinkRequestData = {
                    id: documentValue ?? this.productDetail?.StockId ?? this.productDetail?.ProductId ?? '',
                    contractId: this.contractId,
                    itemName: this.productDetail?.Name ?? '',
                    serialNumber: this.productDetail?.SerialNumber ?? '',
                    customerId: this.customerId,
                    lineItemId: this.productDetail?.Id ?? ''
                };

                // For public (asset tag) requests, getCurrentUser is null — read orgType and orgId from store instead.
                // For authenticated requests, fall back to localStorage as before.
                const storedOrgType = productState.orgType;
                const organizationType: string = storedOrgType || (this.appMediatorService.localStorageService?.getCurrentUser?.organizationType as string);
                // orgId from store is set by the asset tag effect for public requests;
                // it is passed as x-organization-id header to the getpdf endpoint.
                const orgId = productState.orgId;
                this.documentParams = {
                    customerId: this.customerId,
                    contractId: this.contractId,
                    orgType: organizationType,
                    documentValue: documentValue,
                    orgId
                };
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.map((sub: Subscription) => sub.unsubscribe());
    }

    /** True when the close button should be rendered (authenticated, non-quick-link view). */
    get showCloseButton(): boolean {
        return !this.isPublicView && !this.appFacadeService.versionToggleService.isStockQuickLinkVersion();
    }

    /** True when the send-document button should be rendered (non-quick-link view). */
    get showSendDocument(): boolean {
        return !this.appFacadeService.versionToggleService.isStockQuickLinkVersion();
    }

    isContentAvailable(propertyName: string): boolean {
        switch (propertyName) {
            case 'Images':
                return this.productDetail && this.productDetail?.Images && this.productDetail?.Images?.length > 0 ? true : false;
            case 'StoredFiles':
                return this.productDetail && this.productDetail?.StoredFiles && this.productDetail?.StoredFiles?.length > 0 ? true : false;
            case 'Name':
                return this.productDetail && this.productDetail?.Name ? true : false;
            case 'SerialNumber':
                return this.productDetail && this.productDetail?.SerialNumber ? true : false;
            case 'Description':
                return this.productDetail && this.productDetail?.Description ? true : false;
            case 'OtherUrls':
                return this.productDetail && this.productDetail?.OtherUrls && this.productDetail?.OtherUrls.length > 0 ? true : false;
            default:
                return false;
        }
    }

    openDocumentUrl(documentType: string): void {
        this.subscriptions.push(
            this.appFacadeService.openPdfdocument(documentType).subscribe((response: DocumentData[]) => {
                if (response.length > 0) {
                    this.appFacadeService.pdf.openPdfinNewTab(response[0].url);
                }
            })
        );
    }

    handleRentalFormReturn(): void {
        this.closeModal();
    }

    // below event can use as use as output decorator
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleSendDocumentFormSubmission($event: SendLinkRequest): void {
        this.closeModal();
    }

    openModal(): void {
        this.modal.open('product-detail-modal');
    }

    closeModal(): void {
        this.modal.close('product-detail-modal');
    }
}
